"use client";

import { type FormEvent, Suspense, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { useLocale, useTranslations } from "next-intl";
import Image from "next/image";
import Link from "next/link";
import { login } from "@/lib/auth";

const BRAND = "#0d1737";

function LoginForm() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const t = useTranslations("LoginPage");
  const locale = useLocale();

  const rawRedirect = searchParams.get("redirect");
  const redirectTo = (() => {
    if (!rawRedirect) return `/${locale}/home`;
    if (rawRedirect.startsWith("http://") || rawRedirect.startsWith("https://")) {
      try {
        const u = new URL(rawRedirect);
        return u.pathname + u.search || `/${locale}/home`;
      } catch {
        return `/${locale}/home`;
      }
    }
    return rawRedirect.startsWith("/") ? rawRedirect : `/${locale}/home`;
  })();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await login(email, password);
      router.push(redirectTo);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("error"));
    } finally {
      setLoading(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="mt-8 space-y-5">
      <div>
        <label htmlFor="email" className="block text-sm font-medium text-gray-700">
          {t("email")}
        </label>
        <input
          id="email"
          type="email"
          required
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@company.com"
          className="mt-1 block w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-transparent focus:ring-2 focus:ring-offset-1 focus:outline-none"
          style={{ boxShadow: "none", outline: "none", ["--tw-ring-color" as never]: BRAND }}
        />
      </div>

      <div>
        <label htmlFor="password" className="block text-sm font-medium text-gray-700">
          {t("password")}
        </label>
        <input
          id="password"
          type="password"
          required
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="mt-1 block w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-transparent focus:ring-2 focus:ring-offset-1 focus:outline-none"
          style={{ boxShadow: "none", outline: "none", ["--tw-ring-color" as never]: BRAND }}
        />
        <div className="mt-2 text-right">
          <Link
            href={`/${locale}/forgot-password`}
            className="text-sm font-medium"
            style={{ color: BRAND }}
          >
            {t("forgotPassword")}
          </Link>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={loading}
        className="w-full rounded-lg px-4 py-2.5 text-sm font-medium text-white shadow-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50"
        style={{
          backgroundColor: BRAND,
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLButtonElement).style.backgroundColor = "#081030";
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLButtonElement).style.backgroundColor = BRAND;
        }}
      >
        {loading ? t("signingIn") : t("submit")}
      </button>
    </form>
  );
}

export default function LoginPage() {
  const t = useTranslations("LoginPage");

  return (
    <div className="flex min-h-screen items-center justify-center bg-white">
      <div className="w-full max-w-sm rounded-2xl border border-gray-200 bg-white p-8 shadow-lg">
        <div className="text-center">
          <div className="mx-auto flex w-full justify-center">
            <Image
              src="/construxia-logo.png"
              alt="Construxia"
              width={2286}
              height={294}
              priority
              unoptimized
              className="h-12 w-auto"
            />
          </div>
          <p className="mt-3 text-sm text-slate-500">{t("subtitle")}</p>
        </div>

        <Suspense fallback={null}>
          <LoginForm />
        </Suspense>
      </div>
    </div>
  );
}
